import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, TrendingUp, Image, Link2, FileText, Sparkles, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const Guides = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const guides = [
    {
      title: "Getting Started with Seoro",
      icon: BookOpen,
      description: "Complete setup guide: connect your website, integrate Google Search Console, and publish your first article",
      category: "Getting Started",
      id: "getting-started-guide"
    },
    {
      title: "Content Generation Mastery",
      icon: FileText,
      description: "Learn how to create SEO-optimized articles, customize settings, and manage your content effectively",
      category: "Content",
      id: "content-guide"
    },
    {
      title: "SEO Optimization Tips",
      icon: TrendingUp,
      description: "Best practices for keyword research, internal linking, and maximizing your search rankings",
      category: "SEO",
      id: "seo-guide"
    },
    {
      title: "AI Image Generation",
      icon: Image,
      description: "Create high-quality images in multiple styles to enhance your articles and engage readers",
      category: "Content",
      id: "images-guide"
    },
    {
      title: "Backlink Strategy",
      icon: Link2,
      description: "How to claim premium backlinks, track placements, and build domain authority automatically",
      category: "Backlinks",
      id: "backlinks-guide"
    },
    {
      title: "LLMs.txt for AI Visibility",
      icon: Sparkles,
      description: "Generate AI-friendly files to help chatbots and search engines understand your website better",
      category: "SEO",
      id: "llms-guide"
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Calculate header height dynamically
      const getHeaderHeight = () => {
        const header = document.querySelector('header');
        return header ? header.offsetHeight + 20 : 100; // Add 20px padding
      };
      
      const headerOffset = getHeaderHeight();
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle hash links on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const id = hash.substring(1);
        scrollToSection(id);
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Guides & Tutorials - Learn How to Use Seoro | Step-by-Step Guides"
        description="Step-by-step guides to help you get the most out of Seoro. Learn content generation, SEO optimization, backlink strategy, AI image generation, and LLM.txt setup with detailed tutorials."
        keywords="Seoro guides, SEO tutorials, content generation guide, backlink guide, AI image guide, LLM.txt tutorial, how to use Seoro"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Guides & Tutorials
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Step-by-step guides to help you get the most out of Seoro.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map((guide, index) => {
              const Icon = guide.icon;
              return (
                <button
                  key={index}
                  onClick={() => scrollToSection(guide.id)}
                  className="bg-muted/30 rounded-xl p-8 hover:bg-muted/50 transition-all hover:shadow-lg group text-left cursor-pointer w-full"
                >
                  <div className="flex items-start gap-4">
                    <Icon className="h-8 w-8 text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-primary mb-2 block">
                        {guide.category}
                      </span>
                      <h3 className="text-xl font-bold text-foreground mb-2">{guide.title}</h3>
                      <p className="text-muted-foreground">{guide.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Getting Started Guide */}
          <div id="getting-started-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Getting Started with Seoro</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Step 1: Sign Up and Connect Your Website</h3>
                <p className="leading-relaxed">
                  Start by signing up with Google—it's quick and secure. Once you're in, you'll be prompted to connect your website. 
                  Enter your domain (e.g., example.com) and we'll analyze your niche to understand your business and target audience.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Step 2: Connect Google Search Console</h3>
                <p className="leading-relaxed">
                  Link your Google Search Console account to unlock real performance tracking. This gives you actual data on clicks, 
                  impressions, rankings, and indexing status—not estimates. You'll see exactly how your content is performing in search results.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Step 3: Set Up Your CMS Integration</h3>
                <p className="leading-relaxed">
                  Connect your CMS platform (Shopify, WordPress, or Webflow) to enable one-click publishing. Framer integration coming soon! This integration allows you 
                  to publish articles directly from Seoro without copying and pasting. The setup takes just a few minutes.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Step 4: Generate Your First Article</h3>
                <p className="leading-relaxed">
                  Navigate to the "My Content" tab and click "Generate Article". Customize your settings—choose your language, writing 
                  style, word count, and image preferences. Our AI will create an SEO-optimized article tailored to your business. 
                  Review it, make any edits, and publish with one click.
                </p>
              </div>
            </div>
          </div>

          {/* Content Generation Guide */}
          <div id="content-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Content Generation Mastery</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Understanding Article Settings</h3>
                <p className="leading-relaxed mb-3">
                  Before generating, you can customize every aspect of your article:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Language:</strong> Choose from 76 supported languages. Our AI writes naturally in your target language, not translations.</li>
                  <li><strong className="text-foreground">Writing Style:</strong> Select professional, casual, or technical tone to match your brand voice.</li>
                  <li><strong className="text-foreground">Word Count:</strong> Set your preferred length (typically 800-2000 words for optimal SEO).</li>
                  <li><strong className="text-foreground">Image Style:</strong> Choose realistic, cartoon, or other styles. Images are automatically inserted throughout your content.</li>
                  <li><strong className="text-foreground">Article Format:</strong> Blog post, guide, listicle, or review—pick what works best for your content strategy.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">What Makes Our Content SEO-Optimized?</h3>
                <p className="leading-relaxed">
                  Every article we generate includes strategic keyword optimization, natural internal or external links, SEO-friendly 
                  structure with proper headings (H1, H2, H3), and meta-friendly formatting. The content reads naturally to humans while 
                  being optimized for search engines.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Reviewing and Editing</h3>
                <p className="leading-relaxed">
                  After generation, you can review the full article, edit any section, adjust images, or regenerate if needed. Once you're 
                  satisfied, publish directly to your connected CMS platform with one click. All your articles are saved in the "My Content" 
                  tab for easy access and management.
                </p>
              </div>
            </div>
          </div>

          {/* SEO Guide */}
          <div id="seo-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">SEO Optimization Tips</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Keyword Research Strategy</h3>
                <p className="leading-relaxed">
                  Our AI analyzes your niche and competitors to identify high-opportunity keywords. We focus on keywords with good search 
                  volume and lower competition, giving you the best chance to rank. Each article targets specific keywords naturally 
                  throughout the content.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Internal Linking Best Practices</h3>
                <p className="leading-relaxed">
                  Every article automatically includes strategic internal links to other relevant pages on your site. This helps search 
                  engines understand your site structure, distributes page authority, and keeps readers engaged by guiding them to related content.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Content Structure for Rankings</h3>
                <p className="leading-relaxed">
                  Our articles follow SEO best practices: clear H1 headings, well-organized H2/H3 subheadings, proper paragraph length, 
                  and strategic keyword placement. This structure helps search engines understand your content and improves readability for users.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Tracking Your Performance</h3>
                <p className="leading-relaxed">
                  Use Google Search Console integration to track real performance data. Monitor clicks, impressions, average position, and 
                  click-through rates. This data helps you understand what's working and optimize your content strategy over time.
                </p>
              </div>
            </div>
          </div>

          {/* Image Generation Guide */}
          <div id="images-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Image className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">AI Image Generation</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Multiple Image Styles</h3>
                <p className="leading-relaxed">
                  We offer multiple image styles including realistic, cartoon, and more. Choose the style that best matches your brand 
                  and content tone. All images are generated using the newest, most advanced AI image models for the highest quality results.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Automatic Image Placement</h3>
                <p className="leading-relaxed">
                  Images are automatically inserted throughout your articles at strategic points to break up text and enhance readability. 
                  You can adjust the quantity and placement during the article generation process.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Article-Relevant Images</h3>
                <p className="leading-relaxed">
                  Our AI analyzes your article content and generates images that are relevant to the topic. This ensures your visuals 
                  complement your text and provide value to readers, not just decoration.
                </p>
              </div>
            </div>
          </div>

          {/* Backlinks Guide */}
          <div id="backlinks-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Link2 className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Backlink Strategy</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">How Premium Backlinks Work</h3>
                <p className="leading-relaxed">
                  Every month, you receive 10 premium backlinks from high-authority websites (DA 70+). These aren't low-quality directory 
                  listings—they're placements on relevant, high-quality sites that actually boost your domain authority and search rankings.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Claiming Your Backlinks</h3>
                <p className="leading-relaxed">
                  Navigate to the "Backlinks" tab in your dashboard. You'll see available backlink opportunities with details about the 
                  source domain, anchor text, and placement context. Click "Claim" on any backlink you want. Once claimed, we handle the 
                  entire placement process—no outreach, negotiation, or manual work required.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Tracking and Transparency</h3>
                <p className="leading-relaxed">
                  You have full visibility into every backlink placement. See the source domain, anchor text, placement date, and status. 
                  Monitor your domain authority growth over time as these high-quality backlinks boost your rankings. All tracking happens 
                  automatically in your dashboard.
                </p>
              </div>
            </div>
          </div>

          {/* LLMs.txt Guide */}
          <div id="llms-guide" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">LLMs.txt for AI Visibility</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">What is LLMs.txt and Why It Matters</h3>
                <p className="leading-relaxed">
                  LLMs.txt is a file that helps AI systems understand your website. When someone asks ChatGPT or other AI chatbots about 
                  your business, or when AI-powered search engines try to understand your content, this file provides a clear, organized 
                  summary. It's like having a perfect elevator pitch that AI can read and understand instantly.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Generating Your LLMs.txt File</h3>
                <p className="leading-relaxed">
                  Go to the "LLMs.txt" tab in your dashboard. Enter your website URL and click "Generate". Our system analyzes your entire 
                  website—content, structure, and purpose—and creates a comprehensive LLMs.txt file in seconds. The file is optimized for 
                  AI understanding while accurately representing your business.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Adding to Your Website</h3>
                <p className="leading-relaxed">
                  Once generated, download the file and upload it to your website's root directory. The file must be named exactly "llms.txt" 
                  and placed at yoursite.com/llms.txt. We provide step-by-step guides for WordPress, Shopify, Webflow, and other platforms 
                  in the LLMs.txt tab. The setup is quick and only needs to be done once (or when you make major business changes).
                </p>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Need More Help?</h2>
            <p className="text-muted-foreground mb-6">
              Check out our comprehensive documentation or contact our support team for assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/docs')}
                variant="outline"
                className="rounded-full"
              >
                View Documentation
              </Button>
              <Button
                onClick={() => navigate('/support')}
                className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Guides;


